import { useEffect } from 'react'
import * as turf from '@turf/turf'
import { theme } from '../../config/theme'

const RadiusCircle = ({ map, center, radius, visible = true }) => {
  // Update radius circle when props change
  useEffect(() => {
    if (!map) return

    // Function to add/update circle
    const updateCircle = () => {
      if (!center || !radius || !visible) {
        // Remove circle if not visible or missing data
        try {
          if (map.getSource('radius')) {
            if (map.getLayer('radius')) {
              map.removeLayer('radius')
            }
            map.removeSource('radius')
          }
        } catch (error) {
          // Silently ignore cleanup errors
        }
        return
      }

      const [lng, lat] = center
      
      // Validate coordinates
      if (isNaN(lng) || isNaN(lat)) return

      // Create a circle using turf.js
      const centerPoint = turf.point([lng, lat])
      const circle = turf.circle(centerPoint, radius, {
        steps: 64,
        units: 'miles'
      })

      // Check if source exists with proper error handling
      let sourceExists = false
      try {
        sourceExists = !!map.getSource('radius')
      } catch (error) {
        // getSource failed, assume source doesn't exist
        sourceExists = false
      }

      if (!sourceExists) {
        try {
          map.addSource('radius', {
            type: 'geojson',
            data: circle
          })

          // Add the layer if it doesn't exist
          if (!map.getLayer('radius')) {
            map.addLayer({
              id: 'radius',
              type: 'fill',
              source: 'radius',
              paint: {
                'fill-color': theme.colors.primary[500],
                'fill-opacity': 0.15,
                'fill-outline-color': theme.colors.primary[600]
              }
            })
          }
        } catch (error) {
          console.warn('Failed to create radius source/layer:', error)
        }
      } else {
        // Update the source data with the new circle
        try {
          const source = map.getSource('radius')
          if (source) {
            source.setData(circle)
          }
        } catch (error) {
          console.warn('Failed to update radius data, attempting to recreate:', error)
          // If update fails, try recreating
          try {
            if (map.getLayer('radius')) {
              map.removeLayer('radius')
            }
            map.removeSource('radius')
            
            map.addSource('radius', {
              type: 'geojson',
              data: circle
            })
            
            map.addLayer({
              id: 'radius',
              type: 'fill',
              source: 'radius',
              paint: {
                'fill-color': theme.colors.primary[500],
                'fill-opacity': 0.15,
                'fill-outline-color': theme.colors.primary[600]
              }
            })
          } catch (recreateError) {
            console.warn('Failed to recreate radius circle:', recreateError)
          }
        }
      }
    }

    // Check if map is ready
    if (map.isStyleLoaded()) {
      updateCircle()
    } else {
      // Wait for map to load
      const onStyleLoad = () => updateCircle()
      map.on('styledata', onStyleLoad)
      
      return () => {
        map.off('styledata', onStyleLoad)
      }
    }
  }, [map, center, radius, visible])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map && map.getSource) {
        try {
          if (map.getSource('radius')) {
            if (map.getLayer('radius')) {
              map.removeLayer('radius')
            }
            map.removeSource('radius')
          }
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    }
  }, [map])

  // This component doesn't render anything visible
  return null
}

export default RadiusCircle